import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { App, Dropdown } from 'antd'
import TrashIcon from '@/assets/trash.svg?react'
import { CONTENT_LANGUAGES, contentLangLabel } from '../languages'

export default function SupportedLanguagesSelect({
  value,
  default_language,
  disabled,
  onChange,
  children,
}: {
  value: string[]
  default_language: string
  disabled?: boolean
  onChange: (langs: string[], defaultLang: string) => void
  /** 触发下拉的自定义节点 */
  children?: ReactNode
}) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [open, setOpen] = useState(false)

  const added = useMemo(
    () => CONTENT_LANGUAGES.filter((l) => value.includes(l.code)),
    [value],
  )
  const available = useMemo(
    () => CONTENT_LANGUAGES.filter((l) => !value.includes(l.code)),
    [value],
  )

  const remove = (code: string) => {
    if (code === default_language) return
    onChange(
      value.filter((c) => c !== code),
      default_language,
    )
  }

  const add = (code: string) => {
    if (value.includes(code)) return
    onChange([...value, code], default_language)
  }

  const setDefault = (code: string) => {
    if (!value.includes(code)) return
    onChange(value, code)
    message.success(
      t('versionForm.setDefaultToast', { lang: contentLangLabel(code) }),
    )
  }

  const menu = (
    <div
      className="langs-select-panel"
      style={{
        width: 220,
        maxHeight: 360,
        overflow: 'auto',
        background: 'rgba(255, 255, 255, 0.85)',
        border: '1px solid var(--color-border-line)',
        borderRadius: 14,
        boxShadow: '0px 16px 22px -6px rgba(28, 28, 28, 0.16)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        padding: 10,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 10,
        }}
      >
        <SectionLabel>{t('versionForm.langsAdded')}</SectionLabel>
        {added.map((l) => {
          const isDefault = l.code === default_language
          return (
            <div key={l.code} style={langRowStyle}>
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  minWidth: 0,
                }}
              >
                <span style={langTextStyle}>{l.label}</span>
                {isDefault ? (
                  <span style={defaultBadgeYellow}>DEFAULT</span>
                ) : (
                  !disabled && (
                    <button
                      type="button"
                      onClick={() => setDefault(l.code)}
                      style={defaultBadgeOutline}
                    >
                      DEFAULT
                    </button>
                  )
                )}
              </div>
              {!disabled && !isDefault && (
                <button
                  type="button"
                  onClick={() => remove(l.code)}
                  style={iconGhost}
                  aria-label="remove"
                >
                  <TrashIcon width={14} height={14} />
                </button>
              )}
            </div>
          )
        })}

        <div
          style={{
            height: 1,
            background: '#EFEFEF',
            alignSelf: 'stretch',
          }}
        />

        <SectionLabel>{t('versionForm.langsAvailable')}</SectionLabel>
        {available.map((l) => (
          <button
            key={l.code}
            type="button"
            disabled={disabled}
            onClick={() => add(l.code)}
            style={{
              ...langRowStyle,
              height: 24,
              padding: '0 8px',
              border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              textAlign: 'left',
              color: '#000',
              font: 'inherit',
            }}
          >
            <span style={langTextStyle}>{l.label}</span>
          </button>
        ))}
        {available.length === 0 && (
          <div style={{ padding: '6px 8px', fontSize: 12, color: 'rgba(0,0,0,0.35)' }}>
            —
          </div>
        )}
      </div>
    </div>
  )

  if (!children) return null

  return (
    <Dropdown
      open={disabled ? false : open}
      onOpenChange={setOpen}
      popupRender={() => menu}
      trigger={['click']}
      placement="bottomLeft"
    >
      {children}
    </Dropdown>
  )
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        fontSize: 13,
        lineHeight: '20px',
        color: 'rgba(0, 0, 0, 0.4)',
      }}
    >
      {children}
    </div>
  )
}

const langRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'stretch',
  gap: 16,
  padding: '6px 8px',
  borderRadius: 8,
  background: 'transparent',
}

const langTextStyle: CSSProperties = {
  fontSize: 13,
  lineHeight: '20px',
  color: '#000',
}

const defaultBadgeYellow: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 5px',
  height: 18,
  borderRadius: 4,
  background: 'rgba(253, 199, 0, 0.8)',
  fontSize: 12,
  lineHeight: '18px',
  color: '#000',
  flexShrink: 0,
}

const defaultBadgeOutline: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 8px',
  height: 22,
  borderRadius: 6,
  border: '1px solid var(--color-border-line)',
  background: 'rgba(255, 255, 255, 0.85)',
  fontSize: 12,
  lineHeight: '18px',
  color: '#000',
  cursor: 'pointer',
  flexShrink: 0,
}

const iconGhost: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 0,
  width: 14,
  height: 14,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}
